import { useMutation } from '@apollo/client';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Upload, X } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CIRCLES } from '../../constants/circles.constants';
import type {
  CircleFieldsFragment,
  UpdateCircleInput,
} from '../../gql/graphql';
import { UPDATE_CIRCLE } from '../../graphql/resolvers/circleMutations/updateCircle.gql';
import { parseCircleCsv } from '../../helpers/parseCircleCsv';
import { parseCircleMarkdown } from '../../helpers/parseCircleMarkdown';
import ButtonComponent from '../UI/Button/Button.component';

interface ManageCircleModalProps {
  open: boolean;
  onClose: () => void;
  circle: CircleFieldsFragment;
}

const ManageCircleModal = ({
  open,
  onClose,
  circle,
}: ManageCircleModalProps) => {
  const mdFileInputRef = useRef<HTMLInputElement>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(circle.name);
  const [currentMembers, setCurrentMembers] = useState<string[]>(
    circle.members,
  );
  const [newMemberInputs, setNewMemberInputs] = useState<string[]>(['']);
  const [fileMembers, setFileMembers] = useState<string[] | null>(null);
  const [removedMembers, setRemovedMembers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [updateCircle, { loading }] = useMutation(UPDATE_CIRCLE);

  useEffect(() => {
    if (open) {
      setName(circle.name);
      setCurrentMembers(circle.members);
      setNewMemberInputs(['']);
      setFileMembers(null);
      setRemovedMembers([]);
      setError(null);
    }
  }, [open, circle]);

  const handleRemoveExistingMember = (username: string) => {
    setCurrentMembers((prev) => prev.filter((m) => m !== username));
    setRemovedMembers((prev) => [...prev, username]);
  };

  const handleUndoRemove = (username: string) => {
    setCurrentMembers((prev) => [...prev, username]);
    setRemovedMembers((prev) => prev.filter((m) => m !== username));
  };

  const handleNewMemberChange = (index: number, value: string) => {
    setNewMemberInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveNewInput = (index: number) => {
    if (newMemberInputs.length <= 1) return;
    setNewMemberInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddNewInput = () => {
    if (newMemberInputs.length >= 20) return;
    setNewMemberInputs((prev) => [...prev, '']);
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    parser: (raw: string) => string[],
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parser(text);
      if (parsed.length === 0) {
        setError(CIRCLES.ERRORS.NO_VALID_USERNAMES);
        return;
      }
      setFileMembers(parsed);
      setError(null);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    setError(null);
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError(CIRCLES.ERRORS.NAME_REQUIRED);
      return;
    }
    if (trimmedName.length > 90) {
      setError(CIRCLES.ERRORS.NAME_MAX_LENGTH);
      return;
    }

    const addMembers = [
      ...newMemberInputs
        .map((m) => m.trim().replace(/^@/, ''))
        .filter((m) => m.length > 0),
      ...(fileMembers || []),
    ];

    const uniqueNewMembers = [...new Set(addMembers)].filter(
      (m) => !currentMembers.includes(m),
    );

    const nameChanged = trimmedName !== circle.name;
    const hasRemovals = removedMembers.length > 0;
    const hasAdditions = uniqueNewMembers.length > 0;

    if (!nameChanged && !hasRemovals && !hasAdditions) {
      onClose();
      return;
    }

    try {
      const input: UpdateCircleInput = { id: circle.id };
      if (nameChanged) input.name = trimmedName;
      if (hasRemovals) input.removeMembers = removedMembers;
      if (hasAdditions) input.addMembers = uniqueNewMembers;

      const { data } = await updateCircle({ variables: { input } });

      if (data?.updateCircle) {
        onClose();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : CIRCLES.ERRORS.FAILED_UPDATE,
      );
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="create-circle-modal__overlay" />
        <Dialog.Content className="create-circle-modal__content">
          <Dialog.Title className="create-circle-modal__title">
            {CIRCLES.MODAL.MANAGE_TITLE}
          </Dialog.Title>

          <div className="create-circle-modal__field">
            <label htmlFor="manage-circle-name">
              {CIRCLES.MODAL.NAME_LABEL}
            </label>
            <input
              id="manage-circle-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={90}
            />
          </div>

          <p className="create-circle-modal__members-label">
            {CIRCLES.MODAL.CURRENT_MEMBERS} ({currentMembers.length})
          </p>
          <div className="manage-circle__members-chips">
            {currentMembers.map((username) => (
              <span key={username} className="manage-circle__member-chip">
                {username}
                <button
                  type="button"
                  onClick={() => handleRemoveExistingMember(username)}
                  aria-label={`Remove ${username}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>

          {removedMembers.length > 0 && (
            <div className="manage-circle__removed">
              <p className="manage-circle__removed-label">
                {CIRCLES.MODAL.REMOVING} ({removedMembers.length}):
              </p>
              <div className="manage-circle__members-chips">
                {removedMembers.map((username) => (
                  <span
                    key={username}
                    className="manage-circle__member-chip manage-circle__member-chip--removed"
                  >
                    {username}
                    <button
                      type="button"
                      onClick={() => handleUndoRemove(username)}
                      aria-label={`Undo remove ${username}`}
                    >
                      {CIRCLES.MODAL.UNDO}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <p className="create-circle-modal__members-label create-circle-modal__members-label--spaced">
            {CIRCLES.MODAL.ADD_MEMBERS_LABEL}
          </p>

          {fileMembers ? (
            <>
              <div className="create-circle-modal__file-chips">
                {fileMembers.map((username) => (
                  <span key={username} className="chip">
                    {username}
                  </span>
                ))}
              </div>
              <p className="create-circle-modal__file-badge">
                {fileMembers.length} {CIRCLES.MODAL.FROM_FILE}
              </p>
              <button
                type="button"
                className="create-circle-modal__add-member-btn"
                onClick={() => setFileMembers(null)}
              >
                {CIRCLES.MODAL.CLEAR_FILE}
              </button>
            </>
          ) : (
            <>
              <div className="create-circle-modal__members-list">
                {newMemberInputs.map((value, index) => (
                  <div key={index} className="create-circle-modal__member-row">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        handleNewMemberChange(index, e.target.value)
                      }
                      placeholder={CIRCLES.MODAL.USERNAME_PLACEHOLDER}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveNewInput(index)}
                      aria-label={CIRCLES.ARIA.REMOVE_INPUT}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="create-circle-modal__add-member-btn"
                onClick={handleAddNewInput}
                disabled={newMemberInputs.length >= 20}
              >
                <Plus size={14} />
                {CIRCLES.MODAL.ADD_MEMBER}
                {newMemberInputs.length >= 20 && CIRCLES.MODAL.ADD_MEMBER_MAX}
              </button>
            </>
          )}

          <div className="create-circle-modal__file-upload">
            <div className="create-circle-modal__file-row">
              <button
                type="button"
                className="create-circle-modal__file-btn"
                onClick={() => mdFileInputRef.current?.click()}
              >
                <Upload size={14} />
                {CIRCLES.MODAL.UPLOAD_MD}
              </button>
              <span className="create-circle-modal__file-hint">
                {CIRCLES.MODAL.HINT_MD}
              </span>
            </div>
            <div className="create-circle-modal__file-row">
              <button
                type="button"
                className="create-circle-modal__file-btn"
                onClick={() => csvFileInputRef.current?.click()}
              >
                <Upload size={14} />
                {CIRCLES.MODAL.UPLOAD_CSV}
              </button>
              <span className="create-circle-modal__file-hint">
                {CIRCLES.MODAL.HINT_CSV}
              </span>
            </div>
            <input
              ref={mdFileInputRef}
              type="file"
              accept=".md"
              onChange={(e) => handleFileUpload(e, parseCircleMarkdown)}
              className="visually-hidden"
            />
            <input
              ref={csvFileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => handleFileUpload(e, parseCircleCsv)}
              className="visually-hidden"
            />
          </div>

          {error && <p className="create-circle-modal__error">{error}</p>}

          <div className="create-circle-modal__footer">
            <ButtonComponent variant="secondary" onClick={onClose}>
              {CIRCLES.BUTTONS.CANCEL}
            </ButtonComponent>
            <ButtonComponent
              variant="primary"
              onClick={handleSave}
              loading={loading}
              disabled={loading}
            >
              {CIRCLES.BUTTONS.SAVE}
            </ButtonComponent>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ManageCircleModal;
